// AI-823 Tarot Theater state machine.
//
// A PURE reducer state machine driving the tarot card "theater" sequence:
// shuffle -> (bad question detour) -> pick a card -> flying -> jiggle ->
// flip -> land -> tarot idle.
//
// The two compound transitions (advance-shuffle and advance-jiggle) are
// modeled as latched booleans in extended state and re-evaluated by private
// helpers after any event that could satisfy their guard, so latches hold
// regardless of the order in which their conditions arrive.

// Timing/semantic constants for the theater. These live with the reducer so
// the statechart's durations have a single source of truth, but the PURE
// reducer never reads them — the driving hook imports these to schedule the
// MIN_SHUFFLE_ELAPSED / FLYING_TIMER / MIN_JIGGLE_ELAPSED / LAND_COMPLETE
// events. Values mirror the old TarotPickACard implementation.
export const MIN_SHUFFLE_MS = 2000
export const MIN_JIGGLE_MS = 3000
export const FLYING_MS = 600 // old TarotPickACard PICK_A_CARD_T_FLY_MS
export const LANDED_HOLD_MS = 350

export type TarotPhase =
  | 'idle'
  | 'shuffle'
  | 'bad_question_ui'
  | 'pick_a_card'
  | 'flying'
  | 'jiggle'
  | 'flip'
  | 'land'
  | 'tarot_idle'

// True for exactly the phases where the theater overlay is on screen, so the
// underlying chat content (structured tarot reading, title/date, user bubble)
// must be hidden. This includes `shuffle`: the backend's tarot doc streams in
// during shuffle (it's what latches good_question), so excluding shuffle lets
// the chosen card's title/date paint behind the deck and leak the answer before
// the reveal (AI-882 issue 1). idle = no session; tarot_idle = reading already
// shown, so both render underlying content normally.
export const isTheaterActivePhase = (phase: TarotPhase): boolean => phase !== 'idle' && phase !== 'tarot_idle'

export interface TarotTheaterState {
  phase: TarotPhase
  shuffleEnteredAt: number | null
  backendAccepted: boolean
  minShuffleElapsed: boolean
  minJiggleElapsed: boolean
  isStreaming: boolean
  frontImagePreloaded: boolean
  frontImageUrl: string | null
  consumedPullId: string | null
}

export const initialTarotTheaterState: TarotTheaterState = {
  phase: 'idle',
  shuffleEnteredAt: null,
  backendAccepted: false,
  minShuffleElapsed: false,
  minJiggleElapsed: false,
  isStreaming: false,
  frontImagePreloaded: false,
  frontImageUrl: null,
  consumedPullId: null,
}

export type TarotEvent =
  | { type: 'ENTER_NEW_TAROT' }
  | { type: 'ENTER_EXISTING_TAROT' }
  | { type: 'ENTER_EXISTING_TAROT_NO_QUESTION' }
  | { type: 'BACKEND_BAD_QUESTION' }
  | { type: 'BACKEND_GOOD_QUESTION' }
  | { type: 'TAROT_DOC'; pullId: string; frontImageUrl: string }
  | { type: 'MIN_SHUFFLE_ELAPSED'; now: number }
  | { type: 'QUESTION_SUBMIT' }
  | { type: 'CLARIFYING_CARD' }
  | { type: 'USER_CHOOSES_CARD' }
  | { type: 'FLYING_TIMER' }
  | { type: 'MIN_JIGGLE_ELAPSED' }
  | { type: 'STREAMING_CHANGED'; isStreaming: boolean }
  | { type: 'FRONT_IMAGE_READY'; url?: string }
  | { type: 'FLIP_TIMER' }
  | { type: 'LAND_COMPLETE' }
  | { type: 'EXIT_TAROT' }
  | { type: 'BACKEND_ERROR' }

// While in shuffle, advance to pick_a_card once the backend has accepted the
// question AND the minimum shuffle animation time has elapsed. Latches in
// either arrival order.
const maybeAdvanceShuffle = (state: TarotTheaterState): TarotTheaterState => {
  if (
    state.phase === 'shuffle'
    && state.backendAccepted
    && state.minShuffleElapsed
  ) {
    return { ...state, phase: 'pick_a_card' }
  }
  return state
}

// While in jiggle, advance to flip only once all three gates are satisfied:
// the minimum jiggle time elapsed, the streaming response finished, and the
// card's front image is preloaded.
const maybeAdvanceJiggle = (state: TarotTheaterState): TarotTheaterState => {
  if (
    state.phase === 'jiggle'
    && state.minJiggleElapsed
    && !state.isStreaming
    && state.frontImagePreloaded
  ) {
    return { ...state, phase: 'flip' }
  }
  return state
}

export const tarotTheaterReducer = (
  state: TarotTheaterState,
  event: TarotEvent,
): TarotTheaterState => {
  // Each case maps to one edge in the statechart. Events that don't apply to
  // the current phase are ignored by returning the same state reference
  // (identity preserved so useReducer consumers can skip re-renders).
  switch (event.type) {
    // --- idle -> shuffle ---
    case 'ENTER_NEW_TAROT':
      if (state.phase === 'idle') {
        return { ...state, phase: 'shuffle' }
      }
      return state

    // --- idle -> tarot_idle (boot into an existing session with a card) ---
    case 'ENTER_EXISTING_TAROT':
      if (state.phase === 'idle') {
        return { ...state, phase: 'tarot_idle' }
      }
      return state

    // --- idle -> shuffle (boot into an existing session, no accepted question) ---
    case 'ENTER_EXISTING_TAROT_NO_QUESTION':
      if (state.phase === 'idle') {
        return { ...state, phase: 'shuffle' }
      }
      return state

    // --- shuffle: bad-question detour ---
    case 'BACKEND_BAD_QUESTION':
      // Guard on !backendAccepted as defense-in-depth: the completion-boundary
      // detector in the hook can fire in the same render batch a TAROT_DOC
      // arrives (a GOOD question that streamed a doc). If acceptance already
      // latched, this is not a bad question, so do not route to bad_question_ui.
      if (state.phase === 'shuffle' && !state.backendAccepted) {
        return { ...state, phase: 'bad_question_ui' }
      }
      return state

    // --- bad_question_ui -> shuffle (re-ask; clear the shuffle latches) ---
    case 'QUESTION_SUBMIT':
      if (state.phase === 'bad_question_ui') {
        return {
          ...state,
          phase: 'shuffle',
          backendAccepted: false,
          minShuffleElapsed: false,
        }
      }
      return state

    // --- tarot_idle -> pick_a_card (clarifying draw; reset per-draw latches) ---
    case 'CLARIFYING_CARD':
      if (state.phase === 'tarot_idle') {
        return {
          ...state,
          phase: 'pick_a_card',
          minJiggleElapsed: false,
          frontImagePreloaded: false,
          backendAccepted: false,
          // Clear the displayed front image, but KEEP consumedPullId: the prior
          // draw's doc may still linger in streamingDocuments as the new stream
          // starts, and TAROT_DOC is now consumed in pick_a_card. Resetting the
          // id to null would let that stale doc re-latch the old card's image.
          // The fresh clarifying doc carries a NEW pullId, so it is still
          // consumed (dedupe is by-pullId).
          frontImageUrl: null,
        }
      }
      return state

    // --- shuffle -> pick_a_card (compound latch: accepted + min-shuffle) ---
    case 'BACKEND_GOOD_QUESTION':
      if (state.phase === 'shuffle') {
        return maybeAdvanceShuffle({ ...state, backendAccepted: true })
      }
      return state

    case 'TAROT_DOC':
      // The doc arriving also counts as backend acceptance and carries the
      // pull id / front image we'll need downstream.
      //
      // Dedupe by pullId (replaces the old lastAnimatedPullIdRef): a doc whose
      // pullId we've already consumed is a no-op for fields/latches.
      if (event.pullId === state.consumedPullId) {
        return state
      }
      if (state.phase === 'shuffle') {
        return maybeAdvanceShuffle({
          ...state,
          backendAccepted: true,
          frontImageUrl: event.frontImageUrl,
          consumedPullId: event.pullId,
        })
      }
      // Clarifying draw (CLARIFYING_CARD skips shuffle): the doc arrives while
      // the user is already past shuffle. There's no shuffle latch to advance,
      // but we still need the front image / pull id so the jiggle preload gate
      // can open. Re-evaluate the jiggle latch in case the doc is the last gate
      // to land.
      if (
        state.phase === 'pick_a_card'
        || state.phase === 'flying'
        || state.phase === 'jiggle'
      ) {
        return maybeAdvanceJiggle({
          ...state,
          frontImageUrl: event.frontImageUrl,
          consumedPullId: event.pullId,
        })
      }
      return state

    case 'MIN_SHUFFLE_ELAPSED':
      if (state.phase === 'shuffle') {
        return maybeAdvanceShuffle({
          ...state,
          minShuffleElapsed: true,
          shuffleEnteredAt: state.shuffleEnteredAt ?? event.now,
        })
      }
      return state

    // --- pick_a_card -> flying -> jiggle ---
    case 'USER_CHOOSES_CARD':
      if (state.phase === 'pick_a_card') {
        return { ...state, phase: 'flying' }
      }
      return state

    case 'FLYING_TIMER':
      if (state.phase === 'flying') {
        return { ...state, phase: 'jiggle' }
      }
      return state

    // --- jiggle -> flip (compound latch: min-jiggle + !streaming + image) ---
    // STREAMING_CHANGED and FRONT_IMAGE_READY update their gates in any phase
    // (the data can land before jiggle), but only the latch in jiggle advances.
    case 'MIN_JIGGLE_ELAPSED':
      if (state.phase === 'jiggle') {
        return maybeAdvanceJiggle({ ...state, minJiggleElapsed: true })
      }
      return state

    case 'STREAMING_CHANGED':
      return maybeAdvanceJiggle({ ...state, isStreaming: event.isStreaming })

    case 'FRONT_IMAGE_READY':
      return maybeAdvanceJiggle({
        ...state,
        frontImagePreloaded: true,
        frontImageUrl: event.url ?? state.frontImageUrl,
      })

    // --- flip -> land -> tarot_idle ---
    case 'FLIP_TIMER':
      if (state.phase === 'flip') {
        return { ...state, phase: 'land' }
      }
      return state

    case 'LAND_COMPLETE':
      if (state.phase === 'land') {
        return { ...state, phase: 'tarot_idle' }
      }
      return state

    // --- any non-idle phase -> idle (abandon the reading; clean slate) ---
    case 'EXIT_TAROT':
      if (state.phase === 'idle') {
        return state
      }
      return initialTarotTheaterState

    // --- shuffle/pick_a_card/jiggle -> idle (backend failure; clean slate) ---
    // The locked statechart only defines backend_error edges from these three
    // states; from anywhere else it is a no-op.
    case 'BACKEND_ERROR':
      if (
        state.phase === 'shuffle'
        || state.phase === 'pick_a_card'
        || state.phase === 'jiggle'
      ) {
        return initialTarotTheaterState
      }
      return state

    default: {
      // Exhaustiveness check: if a new event type is added without a case,
      // TypeScript will flag the assignment to `never`.
      const exhaustiveCheck: never = event
      return exhaustiveCheck
    }
  }
}
